import { ReactNode, useLayoutEffect, useRef } from 'react';
import {
  TextArea as RACTextArea,
  TextAreaProps as RACTextAreaProps,
  TextField as RACTextField,
  TextFieldProps as RACTextFieldProps,
  composeRenderProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import {
  ClarionDescription,
  ClarionFieldError,
  ClarionLabel,
  fieldBorderStyles,
} from './ClarionField';
import { focusRing } from './utils';

export interface ClarionTextAreaProps extends Omit<RACTextFieldProps, 'children'> {
  label?: ReactNode;
  description?: string;
  errorMessage?: string | ((validation: any) => string);
  placeholder?: string;
  rows?: number;
  cols?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  fill?: boolean;
  size?: 'small' | 'medium' | 'large';
  autoResize?: boolean;
  children?: ReactNode;
  textAreaProps?: RACTextAreaProps;
}

const textAreaStyles = tv({
  extend: focusRing,
  base: 'border rounded-lg bg-white dark:bg-neutral-900/50 font-sans text-sm p-2 transition outline-none',
  variants: {
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    isInvalid: fieldBorderStyles.variants.isInvalid,
    isDisabled: fieldBorderStyles.variants.isDisabled,
    size: {
      small: 'text-xs p-1.5',
      medium: 'text-sm p-2',
      large: 'text-base p-3',
    },
    resize: {
      none: 'resize-none',
      both: 'resize',
      horizontal: 'resize-x',
      vertical: 'resize-y',
    },
    fill: {
      true: 'w-full',
    },
  },
  defaultVariants: {
    size: 'medium',
    resize: 'vertical',
  },
});

const containerStyles = tv({
  base: 'flex flex-col gap-1 font-sans',
  variants: {
    fill: {
      true: 'w-full',
    },
  },
});

export function ClarionTextArea({
  label,
  description,
  errorMessage,
  placeholder,
  rows = 1,
  cols,
  resize = 'vertical',
  fill,
  size = 'medium',
  autoResize = false,
  children,
  className,
  textAreaProps,
  value,
  ...props
}: ClarionTextAreaProps) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const textArea = textAreaRef.current;
    if (!autoResize || !textArea) return;

    const adjustHeight = () => {
      // Reset height to auto to get the correct scrollHeight
      textArea.style.height = 'auto';
      // Set height to scrollHeight to fit content exactly
      textArea.style.height = `${textArea.scrollHeight}px`;

      // If there's still scrolling, add a small buffer
      if (textArea.scrollHeight > textArea.clientHeight) {
        textArea.style.height = `${textArea.scrollHeight + 2}px`;
      }
    };

    adjustHeight();
    textArea.addEventListener('input', adjustHeight);
    return () => {
      textArea.removeEventListener('input', adjustHeight);
    };
    // Re-run when controlled value changes (e.g. sort/dedupe) so height can shrink
  }, [autoResize, value]);

  return (
    <RACTextField
      {...props}
      value={value}
      className={composeRenderProps(className, (className) => containerStyles({ fill, className }))}
    >
      {label && <ClarionLabel>{label}</ClarionLabel>}
      <RACTextArea
        {...textAreaProps}
        ref={textAreaRef}
        placeholder={placeholder}
        rows={rows}
        cols={cols}
        className={composeRenderProps(textAreaProps?.className, (_, renderProps) =>
          textAreaStyles({
            ...renderProps,
            size,
            resize: autoResize ? 'none' : resize,
            fill,
          }),
        )}
      />
      {description && <ClarionDescription>{description}</ClarionDescription>}
      <ClarionFieldError>{errorMessage}</ClarionFieldError>
      {children}
    </RACTextField>
  );
}
